/* eslint-disable @typescript-eslint/no-explicit-any */
import { KeyboardEvent, useEffect, useRef } from 'react';
import { DropDownModal, RadioButton, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { useHotelDirectoryStore } from '../../../modules/store/hotelDirectoryStore';
import { StyledAllRegionList } from './AllRegionList.styles';
import { SELECTED_REGION } from '../../../modules/constants/HotelDirectory.constants';
import clsx from 'clsx';
import { formatNumberByLocale } from '../../../utils/src/utils';
import { useConditionalPageContextSERP } from '../../../hooks';
import { RegionItemProps } from './AllRegionList.types';
import { changeRegionNames, searchCounterValue } from '../HotelDirectoryMap/HotelDirectoryMapHelper';
import { useHotelDirectoryStaticDataContext } from '../../../modules/context/HotelDirectoryStaticDataContext';
import { TRACKING_CONST } from '../../../modules/constants/HotelDirectory.constants';

export const AllRegionList = ({
  openState,
  setOpenState,
  hotelDirectoryCounter,
  setHotelDirectoryCounter,
  setCenter,
}: any) => {
  const compRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const { hotelsLabel, unitedStatesLabel, australiaPILabel, hotelLabel } = useHotelDirectoryStaticDataContext();
  const { currentLocale, headersData } = useConditionalPageContextSERP();
  const { HOTEL_DIRECTORY, REGION_SECTION } = TRACKING_CONST;
  const leisureRegionList = useHotelDirectoryStore(
    (state: { leisureRegionList: any }) => state.leisureRegionList
  ).leisureRegionList;
  const selectedRegion = useHotelDirectoryStore(
    (state: { selectedRegion: any }) => state.selectedRegion
  ).selectedRegion;
  const setHotelDirectoryState = useHotelDirectoryStore(
    (state: { setHotelDirectoryState: any }) => state.setHotelDirectoryState
  );
  const isTabletOrAbove = useCheckBreakpoint('viewportM');

  const onRadioChanged = (regionItem: RegionItemProps) => {
    searchCounterValue(headersData, hotelDirectoryCounter, setHotelDirectoryCounter);
    setOpenState(false);
    setCenter({
      lat: regionItem?.latitude,
      lng: regionItem?.longitude,
    });
    setHotelDirectoryState([SELECTED_REGION], {
      [SELECTED_REGION]: {
        [SELECTED_REGION]: {
          code: regionItem?.code,
          label: regionItem?.label,
          count: regionItem?.count,
          latitude: regionItem?.latitude,
          longitude: regionItem?.longitude,
          customLabel: regionItem?.customLabel,
        },
      },
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>, regionItem: RegionItemProps, index: number): void => {
    if (e.key === 'Enter') {
      onRadioChanged(regionItem);
      return;
    }

    const radioButtons = listContainerRef.current?.querySelectorAll(
      'input[type="radio"]'
    ) as NodeListOf<HTMLInputElement>;
    if (!radioButtons?.length) return;

    const lastIndex = radioButtons.length - 1;
    let targetIndex = -1;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        targetIndex = index < lastIndex ? index + 1 : 0;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        targetIndex = index > 0 ? index - 1 : lastIndex;
        break;
      case 'Tab':
        setOpenState(false);
        return;
    }

    if (targetIndex >= 0) {
      e.preventDefault();
      e.stopPropagation();
      radioButtons[targetIndex]?.focus();
    }
  };
  // Auto-focus selected/first radio button when dropdown opens
  useEffect(() => {
    if (openState && listContainerRef.current) {
      const timeoutId = setTimeout(() => {
        const radioButtons = listContainerRef.current?.querySelectorAll(
          'input[type="radio"]'
        ) as NodeListOf<HTMLInputElement>;
        if (radioButtons?.length) {
          const selectedIndex = leisureRegionList?.findIndex(
            (item: RegionItemProps) => item?.label === selectedRegion?.label
          );
          radioButtons[selectedIndex >= 0 ? selectedIndex : 0]?.focus();
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [openState, leisureRegionList, selectedRegion]);

  // Function to close dropdown when focus leaves both input and dropdown
  const handleBlur = (event: any) => {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && !compRef.current?.contains(relatedTarget)) {
      setOpenState(false);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      // Check if the click target is not within the popup or the button or the icons
      if (!event.target.closest('.all-region-dropdown')) {
        setOpenState(false);
      }
    };

    // Add event listener when the component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Remove event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  useEffect(() => {
    const handleEscKeydown = (event: any) => {
      // Check if the click target is not within the popup or the button or the icons
      if (event.target.closest('.all-region-list-container')) {
        if (event.key === 'Escape') {
          setOpenState(false);
        }
      }
    };

    // Add event listener when the component mounts
    document.addEventListener('keydown', handleEscKeydown);

    // Remove event listener when the component unmounts
    return () => {
      document.removeEventListener('keydown', handleEscKeydown);
    };
  }, []);
  return (
    <StyledAllRegionList ref={compRef} className="container">
      <DropDownModal
        mobileTakeOver={false}
        show={openState}
        className={clsx(
          'all-region-dropdown',
          'mt-4',
          isTabletOrAbove ? 'all-region-dropdown-right' : 'all-region-dropdown-left'
        )}
        mobileHeaderEnabled={false}
        callhandleBlur={handleBlur}
        // Important: The below variable needs to be set to false in the beginning.
        // If it is initially set to true, the click listener will be initialised and this will
        // cause the modal to not open at all
        dropdownModalOpenState={openState}
        scrollDownModalCloseState={true}
        setDropdownModalOpenState={setOpenState}
        handleBlur={true}
        role={'dialog'}
        contentLabel={'Region Dropdown'}
      >
        {/* Adding a custom styling class(custom-padding-top-class) for a padding top of 20px*/}
        <div
          ref={listContainerRef}
          className={clsx('pt-4', 'pb-4', 'radio-btn-outline-class')}
          role="radiogroup"
          aria-label="Select region"
        >
          {leisureRegionList?.map((regionItem: any, index: number) => {
            return (
              <div className="pl-3 pr-3 d-flex justify-content-between radio-btn-wrapper">
                <RadioButton
                  radiobuttonId={`clusterCode${index}`}
                  radiobuttonName="clusterCode"
                  radiobuttonLabel={changeRegionNames(regionItem, unitedStatesLabel, australiaPILabel)}
                  customClickClass="custom_click_track"
                  checked={regionItem?.label === selectedRegion?.label}
                  onChange={() => {
                    onRadioChanged(regionItem);
                  }}
                  value={changeRegionNames(regionItem, unitedStatesLabel, australiaPILabel)}
                  custom_click_track_value={`${HOTEL_DIRECTORY} | ${REGION_SECTION} : ${regionItem?.label} | internal`}
                  disabled={false}
                  labelClassName={'fontWeight'}
                  className={'m-radio-button-sm'}
                  onKeyDown={e => {
                    onKeyDown(e, regionItem, index);
                  }}
                  tabIndexForInput={0}
                  ariaLabelForLink={
                    changeRegionNames(regionItem, unitedStatesLabel, australiaPILabel) +
                    ' ' +
                    formatNumberByLocale(regionItem?.count, currentLocale?.replace('_', '-'))
                  }
                  isRoleAndAriaEnabled={true}
                />
                <div className="region-property-count t-font-alt-xs">
                  {/* TODO: Replace Hotels with AEM labels */}
                  {formatNumberByLocale(regionItem?.count, currentLocale?.replace('_', '-'))}{' '}
                  {regionItem?.count === 1 ? hotelLabel : hotelsLabel}
                </div>
              </div>
            );
          })}
        </div>
      </DropDownModal>
    </StyledAllRegionList>
  );
};

export default AllRegionList;
